import React, { useCallback } from "react";
import styled from "styled-components";
import ReCAPTCHA from "react-google-recaptcha";
import { Row, Col, Form } from "react-bootstrap";

import { FORM_FIELD_KEYS } from "../../../constants/forms";
import { getFieldProps } from "../../../utils/form-fields";

import TextInput from "../../controls/TextInput";
import MaskInput from "../../controls/MaskInput";
import Textarea from "../../controls/Textarea";
import Switch from "../../controls/Switch";
import StyledButton from "../../styled/StyledButton";
import { config } from "../../../../../_config";

const { email } = config;

const StyledContactUsForm = styled(Form)`
  padding-top: 40px;
  padding-bottom: 100px;

  .contact-block-title {
    margin-bottom: 1rem;
    font-family: 'LatoBold';
    font-size: 16px;
    line-height: 28px;
    color: #1B3858;
    opacity: 0.7;
    text-shadow: 1px 1px 0px #FFFFFF;
  }

  .contact-block-link {
    margin-bottom: 1rem;

    a {
      font-family: 'LatoRegular';
      font-size: 16px;
      line-height: 28px;
      color: #078FF1;
    }
  }

  .inputs-block-title {
    margin-bottom: 1rem;
    font-family: 'LatoBold';
    font-size: 14px;
    line-height: 24px;
    color: #1B3858;
    opacity: 0.7;
    text-shadow: 1px 1px 0px #FFFFFF;
  }

  .disclaimer {
    font-family: 'LatoRegular';
    font-size: 10px;
    line-height: 22px;
    text-align: center;
    color: #1B3858;
    opacity: 0.7;
    text-shadow: 1px 1px 0px #FFFFFF;
  }

  button {
    width: 100%;
    margin-top: 74px;
    margin-bottom: 1rem;
  }

  @media ${({ theme }) => theme.devices.tablet} {
    padding-top: 0;
    padding-bottom: 44px;

    .contact-block-title {
      margin-bottom: 0;
    }

    button {
      margin-top: 44px;
    }
  }
`;

// eslint-disable-next-line react/display-name
const ContactUsForm = React.forwardRef(({ values = {}, onReCaptcha, onChange, onSubmit }, ref) => {
  const showOrderId = values[FORM_FIELD_KEYS.questionsOrder] === "true";
  const captchaSiteKey = process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_PUBLIC_KEY;

  const handleSubmitButtonMouseDown = useCallback(() => {
    onChange && onChange({ target: { id: "suspendedSubmit", value: true } });
  }, [onChange]);

  return (
    <StyledContactUsForm
      noValidate
      validated={values.validated}
      onSubmit={onSubmit}
    >
      <Row>
        <Col xs={12} md={6}>
          <div className="contact-block-title">
            <span>Our support e-mail:</span>
          </div>
        </Col>
        <Col xs={12} md={6}>
          <div className="contact-block-link">
            <a href={`mailto:${email}?subject=Mail from Contact Us USA Taxid Site`}>
              {email}
            </a>
          </div>
        </Col>
      </Row>
      <Row>
        <Col xs={12} md={6}>
          <div className="contact-block-title">
            <span>Our support phone is:</span>
          </div>
        </Col>
        <Col xs={12} md={6}>
          <div className="contact-block-link">
            <a href="tel:1-800-317-5781"> 1-800-317-5781</a>
          </div>
        </Col>
      </Row>
      <Row>
        <Col xs={12}><div className="contact-block-link" /></Col>
        <Col xs={12}><div className="contact-block-link" /></Col>
      </Row>
      <Row>
        <Col xs={12} md={6}>
          <TextInput
            {...getFieldProps(FORM_FIELD_KEYS.customerFirstName)}
            value={values[FORM_FIELD_KEYS.customerFirstName]}
            onChange={onChange}
          />
        </Col>
        <Col xs={12} md={6}>
          <TextInput
            {...getFieldProps(FORM_FIELD_KEYS.customerLastName)}
            value={values[FORM_FIELD_KEYS.customerLastName]}
            onChange={onChange}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12} md={6}>
          <MaskInput
            {...getFieldProps(FORM_FIELD_KEYS.customerPhone)}
            value={values[FORM_FIELD_KEYS.customerPhone]}
            onChange={onChange}
          />
        </Col>
        <Col xs={12} md={6}>
          <TextInput
            {...getFieldProps(FORM_FIELD_KEYS.customerEmail)}
            loading={values.verifying}
            value={values[FORM_FIELD_KEYS.customerEmail]}
            onChange={onChange}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <div className="inputs-block-title">
            <span>Have you placed and order with us?</span>
          </div>
        </Col>
        <Col xs={12}>
          <Switch
            {...getFieldProps(FORM_FIELD_KEYS.questionsOrder)}
            value={values[FORM_FIELD_KEYS.questionsOrder]}
            onChange={onChange}
          />
        </Col>
      </Row>
      {showOrderId && (
        <Row>
          <Col xs={12}>
            <TextInput
              {...getFieldProps(FORM_FIELD_KEYS.orderId)}
              value={values[FORM_FIELD_KEYS.orderId]}
              onChange={onChange}
            />
          </Col>
        </Row>
      )}
      <Row>
        <Col xs={12}>
          <Textarea
            {...getFieldProps(FORM_FIELD_KEYS.message)}
            validated={values.validated}
            value={values[FORM_FIELD_KEYS.message]}
            onChange={onChange}
          />
        </Col>
      </Row>
      {captchaSiteKey && (
        <Row>
          <Col xs={12}>
            <ReCAPTCHA
              className="g-recaptcha"
              ref={ref}
              sitekey={captchaSiteKey}
              onChange={onReCaptcha}
            />
          </Col>
        </Row>
      )} 
      <Row>
        <Col xs={0} md={3} />
        <Col xs={12} md={6}>
          <StyledButton
            type="submit"
            id="ContactUsSubmitButton"
            onMouseDown={handleSubmitButtonMouseDown}
            // disabled={!ref?.current?.getValue()}
          >
            Submit
          </StyledButton>
        </Col>
        <Col xs={0} md={3} />
      </Row>
      <Row>
        <Col xs={12}>
          <div className="disclaimer">
            <span>Clicking on <strong>Submit</strong> Application means you agree to our Terms of Service and Privacy Policy.</span>
          </div>
        </Col>
      </Row>
    </StyledContactUsForm>
  );
});

export default ContactUsForm;
